/**
 * Situação de um certificado digital pela data de validade.
 *
 * A mesma régua é usada em três lugares — o painel de certificados do
 * dashboard, a aba Config. PIX e o cadastro de conta corrente — e o backend
 * classifica igual. Manter isso num só arquivo evita que os três divirjam.
 */
export type CertificateStatus = 'ok' | 'expiring' | 'expired' | 'missing' | 'unknown';

/**
 * Antecedência do aviso de renovação, em dias.
 *
 * 30 não é um número arbitrário: é a janela em que o Itaú aceita renovar. Avisar
 * antes disso produziria um pedido que o banco recusa (erro C700).
 */
export const CERTIFICATE_WARNING_DAYS = 30;

/**
 * Dias de calendário até a data "YYYY-MM-DD" (negativo = já passou).
 *
 * Monta o Date a partir dos componentes em vez de `new Date(iso)`: a segunda
 * forma interpreta a string como UTC meia-noite e, em UTC−3, exibe o dia
 * anterior. Um certificado que vence dia 3 apareceria vencendo dia 2.
 */
export const daysUntilISO = (iso: string | null | undefined): number => {
  if (!iso) return 0;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return 0;
  const target = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target.getTime() - today.getTime()) / 86400000);
};

/** Classifica um certificado pela data de validade. */
export const certificateStatus = (iso: string | null | undefined): CertificateStatus => {
  if (!iso) return 'missing';
  const days = daysUntilISO(iso);
  if (days < 0) return 'expired';
  if (days <= CERTIFICATE_WARNING_DAYS) return 'expiring';
  return 'ok';
};

/** Classes do badge por situação (vermelho / âmbar / verde / cinza). */
export const certificateBadgeClass = (status: CertificateStatus): string => {
  switch (status) {
    case 'expired':
      return 'bg-red-50 border-red-200 text-red-700';
    case 'expiring':
      return 'bg-amber-50 border-amber-200 text-amber-700';
    case 'missing':
    case 'unknown':
      return 'bg-slate-50 border-slate-200 text-slate-600';
    default:
      return 'bg-emerald-50 border-emerald-200 text-emerald-700';
  }
};
